/**
 * FormSection — the single section-header pattern for every form/input surface.
 *
 * Before this component the app carried 21 distinct ad-hoc treatments for form
 * section labels (bold paragraphs, bare `<label>`s, five different `<h3>`
 * margin variants, and several sections with no header at all), which made the
 * structure of long forms ambiguous. See issue #164.
 *
 * Typography comes straight from the existing brand tokens — the title is the
 * `heading4` variant (Geist 600 / 1.25rem, `typography.h4`) and the description
 * is `bodySmall` (Geist 400 / 0.875rem, `typography.small`). No new scale.
 *
 * Separation is a warm hairline rule (`border-gray-200` = `#DBD4C9`,
 * `semantic.borderDefault`) above each section, suppressed on the first one.
 * FormSection owns the vertical rhythm between sections, so the container that
 * stacks them must NOT also apply `space-y-*` — that would double the gap.
 *
 *   <div>
 *     <FormSection title="Basic Information">…</FormSection>
 *     <FormSection title="Wines Included" description="Choose which wines…">…</FormSection>
 *   </div>
 */

import type { ReactNode } from 'react';
import { clsx } from 'clsx';

import { Typography } from './Typography';

interface FormSectionProps {
  /** Section title. Rendered with the `heading4` type token. */
  title: string;
  /** Optional one-line explanation shown under the title. */
  description?: string;
  /**
   * Optional controls pinned to the right of the header row (e.g. an
   * "Add score" button). Kept inside the header so it never collides with the
   * page-level top-right action buttons required by CLAUDE.md.
   */
  actions?: ReactNode;
  /**
   * Heading level. Defaults to `h2` (page title is the `h1`); use `h3` for a
   * section nested inside another FormSection.
   */
  titleAs?: 'h2' | 'h3' | 'h4';
  /**
   * Force the separator on or off. Defaults to automatic — the rule is
   * suppressed when the section is the first child of its container. Pass
   * `false` when a sibling (an error banner, say) renders before the first
   * section and would otherwise take the `:first-child` slot.
   */
  divider?: boolean;
  /** Classes for the section element itself. */
  className?: string;
  /** Classes for the wrapper around `children`. Defaults to `space-y-4`. */
  contentClassName?: string;
  /** Omit for a header-only section (a list header with its own controls, say). */
  children?: ReactNode;
}

export const FormSection = ({
  title,
  description,
  actions,
  titleAs = 'h2',
  divider,
  className,
  contentClassName = 'space-y-4',
  children,
}: FormSectionProps) => (
  <section
    className={clsx(
      divider === false
        ? 'mt-6'
        : divider === true
          ? 'mt-6 border-t border-gray-200 pt-6'
          : 'mt-6 border-t border-gray-200 pt-6 first:mt-0 first:border-t-0 first:pt-0',
      className
    )}
  >
    <div className={clsx('flex items-start justify-between gap-4', children !== undefined && 'mb-4')}>
      <div>
        <Typography variant="heading4" as={titleAs} className="text-gray-900">
          {title}
        </Typography>
        {description && (
          <Typography variant="bodySmall" as="p" className="mt-1 text-gray-600">
            {description}
          </Typography>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
    {children !== undefined && <div className={contentClassName || undefined}>{children}</div>}
  </section>
);
